import { useCallback, useEffect, useMemo, useState } from 'react';
import { getDocStore } from '../../store/doc-store.ts';
import type { SyncStatus } from '../../store/sync-provider.ts';
import {
  type AccessProbeTarget,
  accessProbeTargets,
  accessRouteRequiresSignIn,
} from '../layout/access-session.ts';

type AccessStatus =
  | 'checking'
  | 'connected'
  | 'sign_in_required'
  | 'unavailable'
  | 'not_configured';

const SYNC_LABELS: Record<SyncStatus, string> = {
  disabled: 'Not configured',
  connecting: 'Connecting',
  connected: 'Connected',
  disconnected: 'Offline',
};

export function ConnectionStatusSection({
  apiUrl = import.meta.env.VITE_API_URL,
  syncUrl = import.meta.env.VITE_SYNC_URL,
}: {
  apiUrl?: string;
  syncUrl?: string;
} = {}): JSX.Element {
  const syncStatus = getDocStore().useSyncStatus();
  const targets = useMemo(() => accessProbeTargets(apiUrl, syncUrl), [apiUrl, syncUrl]);
  const [accessStatuses, setAccessStatuses] = useState<
    Partial<Record<'api' | 'sync', AccessStatus>>
  >({});
  const [checking, setChecking] = useState(false);

  const refresh = useCallback(async (): Promise<void> => {
    if (targets.length === 0) return;
    setChecking(true);
    setAccessStatuses(
      Object.fromEntries(targets.map((target) => [target.id, 'checking'])) as Partial<
        Record<'api' | 'sync', AccessStatus>
      >,
    );
    const results = await Promise.all(
      targets.map(async (target) => {
        const signInRequired = await accessRouteRequiresSignIn(target.url);
        return [
          target.id,
          signInRequired === true
            ? 'sign_in_required'
            : signInRequired === false
              ? 'connected'
              : 'unavailable',
        ] as const;
      }),
    );
    setAccessStatuses(Object.fromEntries(results));
    setChecking(false);
  }, [targets]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const targetFor = (id: 'api' | 'sync'): AccessProbeTarget | undefined =>
    targets.find((target) => target.id === id);
  const openSignIn = (target: AccessProbeTarget): void => {
    window.open(target.url, '_blank', 'noopener,noreferrer');
  };
  const apiTarget = targetFor('api');
  const syncTarget = targetFor('sync');
  const apiStatus = apiTarget ? (accessStatuses.api ?? 'checking') : 'not_configured';
  const syncAccessStatus = syncTarget ? (accessStatuses.sync ?? 'checking') : 'not_configured';

  return (
    <section className="mb-6 rounded-xl border border-[color:var(--app-border)] bg-[color:var(--app-panel-muted)] p-4">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-medium text-[color:var(--app-text)]">Connections</h3>
          <p className="mt-2 text-xs text-[color:var(--app-text-muted)]">
            Check the protected API and sync link without exposing connection addresses here.
          </p>
        </div>
        <button
          type="button"
          data-testid="settings-connections-refresh"
          disabled={checking || targets.length === 0}
          onClick={() => void refresh()}
          className="ui-button disabled:cursor-not-allowed disabled:opacity-60"
        >
          {checking ? 'Checking…' : 'Check again'}
        </button>
      </div>

      <div className="divide-y divide-[color:var(--app-border)] rounded-lg border border-[color:var(--app-border)] bg-[color:var(--app-panel)]">
        <ConnectionRow
          name="API"
          status={describeAccessStatus(apiStatus)}
          tone={accessTone(apiStatus)}
          action={
            apiTarget && apiStatus === 'sign_in_required' ? (
              <button
                type="button"
                data-testid="settings-api-sign-in"
                onClick={() => openSignIn(apiTarget)}
                className="ui-button ui-button-danger"
              >
                Sign in
              </button>
            ) : undefined
          }
        />
        <ConnectionRow
          name="Sync link"
          status={describeSyncStatus(syncStatus, syncAccessStatus)}
          tone={syncTone(syncStatus, syncAccessStatus)}
          action={
            syncTarget && syncAccessStatus === 'sign_in_required' ? (
              <button
                type="button"
                data-testid="settings-sync-sign-in"
                onClick={() => openSignIn(syncTarget)}
                className="ui-button ui-button-danger"
              >
                Sign in
              </button>
            ) : syncStatus === 'disconnected' ? (
              <button
                type="button"
                data-testid="settings-sync-reconnect"
                onClick={() => window.location.reload()}
                className="ui-button"
              >
                Reconnect
              </button>
            ) : undefined
          }
        />
      </div>
      <p className="mt-3 text-xs text-[color:var(--app-text-soft)]">
        Sync reconnects automatically in the background. Reconnect restarts the app if it remains
        offline.
      </p>
    </section>
  );
}

function ConnectionRow({
  name,
  status,
  tone,
  action,
}: {
  name: string;
  status: string;
  tone: 'neutral' | 'success' | 'warning' | 'danger';
  action?: React.ReactNode;
}): JSX.Element {
  return (
    <div className="flex min-h-14 flex-wrap items-center justify-between gap-3 px-3 py-2.5">
      <p className="text-sm font-medium text-[color:var(--app-text)]">{name}</p>
      <div className="flex items-center gap-3">
        <span className={`ui-chip ${toneClass(tone)} text-[10px] uppercase tracking-[0.14em]`}>
          {status}
        </span>
        {action}
      </div>
    </div>
  );
}

function describeAccessStatus(status: AccessStatus): string {
  if (status === 'checking') return 'Checking';
  if (status === 'connected') return 'Available';
  if (status === 'sign_in_required') return 'Sign-in required';
  if (status === 'unavailable') return 'Unavailable';
  return 'Not configured';
}

function describeSyncStatus(syncStatus: SyncStatus, accessStatus: AccessStatus): string {
  if (accessStatus === 'sign_in_required') return 'Sign-in required';
  if (syncStatus === 'disabled') return 'Not configured';
  return SYNC_LABELS[syncStatus];
}

function accessTone(status: AccessStatus): 'neutral' | 'success' | 'warning' | 'danger' {
  if (status === 'connected') return 'success';
  if (status === 'checking' || status === 'not_configured') return 'neutral';
  if (status === 'sign_in_required') return 'warning';
  return 'danger';
}

function syncTone(
  syncStatus: SyncStatus,
  accessStatus: AccessStatus,
): 'neutral' | 'success' | 'warning' | 'danger' {
  if (accessStatus === 'sign_in_required') return 'warning';
  if (syncStatus === 'connected') return 'success';
  if (syncStatus === 'connecting') return 'warning';
  if (syncStatus === 'disconnected') return 'danger';
  return 'neutral';
}

function toneClass(tone: 'neutral' | 'success' | 'warning' | 'danger'): string {
  if (tone === 'success') return 'ui-chip-success';
  if (tone === 'warning') return 'ui-chip-warning';
  if (tone === 'danger') return 'ui-chip-danger';
  return '';
}
